import { cn } from "@/lib/utils";

interface CardProps {
  children: React.ReactNode;
  className?: string;
  hideBorder?: boolean;
  hoverable?: boolean;
}

const Card = ({
  children,
  className,
  hideBorder = false,
  hoverable = true,
}: CardProps) => {
  return (
    <div
      className={cn(
        "border border-neutral-700 bg-neutral-800 transition-all duration-200 rounded-[20px]",
        hoverable && "hover:bg-neutral-750 hover:border-neutral-600 group",
        className,
        hideBorder && "border-none",
      )}
    >
      {children}
    </div>
  );
};

export default Card;

interface BlurCardProps {
  backing: React.ReactNode;
  /** Sharp overlay content. Omit for a teaser that is only a backdrop. */
  children?: React.ReactNode;
  className?: string;
  blurPx?: number;
  /**
   * Tint over the blurred backing. The white default carries dark copy; pass a
   * dark wash when the card sits on `background` with nothing on top of it,
   * or it reads as a light-grey skeleton.
   */
  washClassName?: string;
}

/**
 * Renders `backing` as a backdrop, blurs + tints it, and stacks `children`
 * sharply on top. Used for "teaser" UI where real content sits behind a
 * frosted overlay.
 */
export const BlurCard = ({
  backing,
  children,
  className,
  blurPx = 30,
  washClassName = "bg-white/50",
}: BlurCardProps) => (
  <div className={cn("relative overflow-hidden rounded-[20px]", className)}>
    {backing}
    <div
      className="absolute inset-0 rounded-[20px]"
      style={{ backdropFilter: `blur(${blurPx}px)` }}
    />
    <div className={cn("absolute inset-0 rounded-[20px]", washClassName)} />
    {children && (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-y-6 px-8 text-center">
        {children}
      </div>
    )}
  </div>
);
